import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DOSSIER_SCENARIO_LABELS,
  DOSSIER_STATUT_LABELS,
  type DossierStatut,
} from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { Label } from '../../components/ui/Label';
import { Spinner } from '../../components/ui/Spinner';
import { useDossier, useDossierHistory, useTransitionDossier, useUpdateDossier } from './hooks';
import { DossierEditForm } from './DossierEditForm';
import { DossierCompletenessPanel } from './DossierCompletenessPanel';
import { useBiens } from '../structure/hooks';
import { StructureTab } from '../structure/StructureTab';
import { CostResolutionTab } from '../referentiels/CostResolutionTab';
import { CalculationTab } from '../calculations/CalculationTab';
import { DocumentsTab } from '../documents/DocumentsTab';
import { MarketDataTab } from '../market-data/MarketDataTab';
import { cn } from '../../lib/cn';

type DetailTab = 'details' | 'structure' | 'couts' | 'calcul' | 'marche' | 'documents';

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-slate-500 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}

function Field({ label, value }: { label: string; value?: string | null }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-800">{value && value.length > 0 ? value : '—'}</dd>
    </div>
  );
}

export function DossierDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const query = useDossier(id);
  const historyQuery = useDossierHistory(id);
  const updateDossier = useUpdateDossier(id ?? '');
  const transition = useTransitionDossier(id ?? '');
  // Read-only count for the completeness guidance (existing biens endpoint).
  const biensQuery = useBiens(id ?? '', { page: 1, pageSize: 1 });

  const [tab, setTab] = useState<DetailTab>('details');
  const [editing, setEditing] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<DossierStatut | null>(null);
  const [justification, setJustification] = useState('');

  if (query.isLoading) {
    return (
      <div className="py-10 text-center">
        <Spinner label="Chargement du dossier…" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="space-y-3">
        <Alert variant="error">{toDisplayError(query.error).message}</Alert>
        <Link to="/dossiers" className="text-sm text-brand-600 hover:underline">
          ← Retour à la liste
        </Link>
      </div>
    );
  }

  const dossier = query.data;

  const confirmTransition = () => {
    if (!pendingTransition) return;
    transition.mutate(
      { toStatut: pendingTransition, justification: justification ? justification : undefined },
      {
        onSuccess: () => {
          setPendingTransition(null);
          setJustification('');
        },
      },
    );
  };

  return (
    <div
      className={cn(
        'space-y-4',
        tab === 'structure' || tab === 'documents' || tab === 'marche' ? 'max-w-5xl' : 'max-w-3xl',
      )}
    >
      <Link to="/dossiers" className="text-sm text-brand-600 hover:underline">
        ← Retour à la liste
      </Link>

      <div
        role="tablist"
        aria-label="Sections du dossier"
        className="flex border-b border-slate-200"
      >
        <TabButton active={tab === 'details'} onClick={() => setTab('details')}>
          Détails
        </TabButton>
        <TabButton active={tab === 'structure'} onClick={() => setTab('structure')}>
          Structure immobilière
        </TabButton>
        <TabButton active={tab === 'couts'} onClick={() => setTab('couts')}>
          Coûts
        </TabButton>
        <TabButton active={tab === 'calcul'} onClick={() => setTab('calcul')}>
          Calcul
        </TabButton>
        <TabButton active={tab === 'marche'} onClick={() => setTab('marche')}>
          Marché
        </TabButton>
        <TabButton active={tab === 'documents'} onClick={() => setTab('documents')}>
          Documents
        </TabButton>
      </div>

      {tab === 'documents' ? (
        <DocumentsTab dossierId={dossier.id} scenario={dossier.scenario} statut={dossier.statut} />
      ) : tab === 'marche' ? (
        <MarketDataTab dossierId={dossier.id} scenario={dossier.scenario} statut={dossier.statut} />
      ) : tab === 'calcul' ? (
        <CalculationTab dossierId={dossier.id} />
      ) : tab === 'couts' ? (
        <CostResolutionTab dossierId={dossier.id} />
      ) : tab === 'structure' ? (
        <StructureTab dossierId={dossier.id} />
      ) : (
        <>
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="font-mono">{dossier.numero}</CardTitle>
                <Badge tone="info">{DOSSIER_STATUT_LABELS[dossier.statut]}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {updateDossier.isError ? (
                <Alert variant="error" details={toDisplayError(updateDossier.error).details}>
                  {toDisplayError(updateDossier.error).message}
                </Alert>
              ) : null}
              {transition.isError ? (
                <Alert variant="error" details={toDisplayError(transition.error).details}>
                  {toDisplayError(transition.error).message}
                </Alert>
              ) : null}

              {editing ? (
                <DossierEditForm
                  dossier={dossier}
                  pending={updateDossier.isPending}
                  onCancel={() => setEditing(false)}
                  onSubmit={(input) =>
                    updateDossier.mutate(input, { onSuccess: () => setEditing(false) })
                  }
                />
              ) : (
                <>
                  <dl className="grid gap-4 sm:grid-cols-2">
                    <Field label="Mandant" value={dossier.mandantNom} />
                    <Field label="Scénario" value={DOSSIER_SCENARIO_LABELS[dossier.scenario]} />
                    <Field label="Statut" value={DOSSIER_STATUT_LABELS[dossier.statut]} />
                    <Field label="Objet" value={dossier.objet} />
                  </dl>
                  <Button onClick={() => setEditing(true)}>Modifier</Button>
                </>
              )}
            </CardContent>
          </Card>

          <DossierCompletenessPanel dossier={dossier} biensCount={biensQuery.data?.total} />

          {/* Workflow control driven entirely by the server-provided availableTransitions. */}
          <Card>
            <CardHeader>
              <CardTitle>Changer le statut</CardTitle>
            </CardHeader>
            <CardContent>
              {dossier.availableTransitions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {dossier.availableTransitions.map((target) => (
                    <Button
                      key={target}
                      variant="secondary"
                      onClick={() => {
                        setJustification('');
                        setPendingTransition(target);
                      }}
                    >
                      → {DOSSIER_STATUT_LABELS[target]}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Aucune transition disponible pour ce statut.
                </p>
              )}
            </CardContent>
          </Card>

          {/* History timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Historique</CardTitle>
            </CardHeader>
            <CardContent>
              {historyQuery.isLoading ? (
                <Spinner label="Chargement de l’historique…" />
              ) : historyQuery.isError ? (
                <Alert variant="error">{toDisplayError(historyQuery.error).message}</Alert>
              ) : historyQuery.data && historyQuery.data.length > 0 ? (
                <ol className="space-y-3">
                  {historyQuery.data.map((t) => (
                    <li key={t.id} className="border-l-2 border-brand-200 pl-3">
                      <p className="text-sm font-medium text-slate-800">
                        {t.fromStatut ? DOSSIER_STATUT_LABELS[t.fromStatut] : '(création)'} →{' '}
                        {DOSSIER_STATUT_LABELS[t.toStatut]}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(t.createdAt).toLocaleString('fr-FR')}
                        {t.justification ? ` — ${t.justification}` : ''}
                      </p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-slate-500">Aucun événement pour le moment.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <ConfirmDialog
        open={pendingTransition !== null}
        onClose={() => setPendingTransition(null)}
        onConfirm={confirmTransition}
        title="Confirmer le changement de statut"
        description={
          pendingTransition
            ? `Passer le dossier au statut « ${DOSSIER_STATUT_LABELS[pendingTransition]} » ?`
            : undefined
        }
        confirmLabel="Confirmer"
        loading={transition.isPending}
      >
        <div>
          <Label htmlFor="justification">Justification (facultatif)</Label>
          <textarea
            id="justification"
            rows={3}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </div>
      </ConfirmDialog>
    </div>
  );
}
